import { Dialog, DialogBody, DialogContent, DialogTrigger, Tooltip } from '@civilgrid/ui/components';
import { type JSX, type ReactNode, useId, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import Icon from '~/components2/Icon';
import rootStore from '../store';
import authorizations, { type Action } from '.';

interface Props {
  /** A single action, or an array that is allowed only when every action is permitted. */
  action?: Action | Action[];
  children: ReactNode;
  message?: JSX.Element;
  /**
   * When unauthorized, render `fallback` (nothing by default) instead of the blocked
   * children. The children never mount, so none of their hooks or effects run — use
   * this for dialogs/modals that fetch data on mount.
   */
  preventRender?: boolean;
  fallback?: ReactNode;
}

export default function Can({ action, message, children, preventRender, fallback }: Props) {
  const { me, myRole } = useSnapshot(rootStore);
  const blockedContentId = useId();
  // Stable key so the memo doesn't recompute on every render for inline array literals.
  const actionKey = Array.isArray(action) ? action.join('|') : action;
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const allowed = useMemo(() => {
    const actions = Array.isArray(action) ? action : action ? [action] : [];
    if (actions.length === 0) {
      return true;
    }
    if (me) {
      return actions.every((a) => authorizations.can(a));
    } else {
      return false;
    }
  }, [me, myRole, actionKey]);

  if (allowed) {
    return children;
  }

  if (preventRender) {
    return fallback ?? null;
  }

  const m = message || (
    <div className="text-sm">
      <span>
        Your current role (<code className=" px-2">{myRole?.name}</code>) is not authorized to perform this action.
      </span>
      <br />
      <div className="mt-4 flex items-center space-x-2">
        Please contact your account admin or click the
        <span className="inline-block px-1">
          <Icon icon="help2" ariaLabel="Help" />
        </span>{' '}
        icon in the top right to contact support.
      </div>
    </div>
  );

  return (
    <div key={`can-${actionKey}`} className="relative w-full">
      {/* Inert keeps the blocked control out of focus and the accessibility tree; it stays visible underneath. */}
      <div id={blockedContentId} className="pointer-events-none" inert>
        {children}
      </div>
      <Dialog>
        <DialogTrigger asChild>
          {/* The trigger overlays the blocked control instead of wrapping it, so no control nests inside the button.
              aria-labelledby reaches inert content, so the button keeps the blocked control's visible label. */}
          <Tooltip
            side="right"
            contentChildren={m}
            triggerClassName="absolute inset-0 cursor-not-allowed after:hidden"
            aria-labelledby={blockedContentId}
            data-testid={`can-blocked-${actionKey}`}
          >
            {null}
          </Tooltip>
        </DialogTrigger>
        <DialogContent
          title="Action cannot be completed"
          className="z-[110] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[100]"
          data-testid="action-not-allowed-dialog"
        >
          <DialogBody className="text-sm">{m}</DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}
